import { bareSubdivisionCode, IPlace } from '../data/countries'
import { ILeaveLawEntry, ILeaveLawSuggestion, LeaveLawKeyEnum, LeaveLawsResponse } from '../types/leave-law'
import { ShortestLeaveIntervalEnum } from '../types/leave-policy'
import { SubscriptionPlan } from '../types/company'

/**
 * Our leave-type names mapped onto the feed's stable keys, lowercased.
 *
 * Both sides drifted: signup creates "Sick Day" while the feed calls the same entitlement "Sick
 * Leave", and admins rename leave types freely. Matching has to be case-insensitive and accept
 * either wording, or a company created by signup would never match its own sick leave.
 *
 * Working Remotely is absent - the feed has no statutory counterpart for it.
 */
const LEAVE_LAW_KEY_BY_NAME: Record<string, LeaveLawKeyEnum> = {
  'paid time off': LeaveLawKeyEnum.paidTimeOff,
  'pto': LeaveLawKeyEnum.paidTimeOff,
  'sick day': LeaveLawKeyEnum.sick,
  'sick leave': LeaveLawKeyEnum.sick,
}

/** The statutory entitlement a leave type corresponds to, or undefined if the feed has no such thing. */
export const leaveLawKeyForName = (leaveTypeName?: string | null): LeaveLawKeyEnum | undefined => LEAVE_LAW_KEY_BY_NAME[leaveTypeName?.trim().toLowerCase() ?? '']

/**
 * The entitlement a company leave type should take its policy from.
 *
 * Paid Time Off and Sick Leave match by alias, because signup and the feed word them differently
 * and admins vary the casing. Every other entitlement - Maternity, Parental, Jury Duty and the
 * rest - must match the feed's own name exactly, so a leave type only picks up a statutory policy
 * when it unambiguously is that thing.
 */
export const findLeaveLawSuggestionForLeaveType = (entry: ILeaveLawEntry | undefined, leaveTypeName?: string | null): ILeaveLawSuggestion | undefined => {
  if (!leaveTypeName) return undefined

  const key = leaveLawKeyForName(leaveTypeName)
  return key ? findLeaveLawSuggestion(entry, key) : entry?.suggestedLeaveTypes?.find((suggestion) => suggestion.name === leaveTypeName)
}

/** The subset of a leave policy a statutory entitlement can decide. */
export interface ILeaveLawPolicy {
  daysPerYear: number
  hasUnlimitedDays: boolean
  isApprovalRequired: boolean
  shortestLeaveInterval: ShortestLeaveIntervalEnum
}

/**
 * The law for a place, or undefined when the feed has nothing for it.
 *
 * A subdivision-level entry wins, then the country-level one. That ordering is what makes the
 * feed's three shapes work from a single rule: Canada and the US have per-state entries only (so a
 * country with no state resolves to nothing), Australia and Germany have a national entry only (so
 * any state falls back to it), and Hungary has neither.
 *
 * Normalising happens here rather than at the call sites: the feed stores bare lowercase suffixes
 * ('wy') while we store full ISO 3166-2 ('US-WY'), and comparing them raw matches nothing.
 */
export const findLeaveLaw = (laws: LeaveLawsResponse, place: IPlace): ILeaveLawEntry | undefined => {
  if (!place.countryCode) return undefined

  const country = place.countryCode.toUpperCase()
  const subdivision = bareSubdivisionCode(place.subdivisionCode)?.toUpperCase()
  const entries = Object.values(laws)

  const inCountry = entries.filter((entry) => entry.countryCode?.toUpperCase() === country)

  const bySubdivision = subdivision ? inCountry.find((entry) => entry.subdivisionCode?.toUpperCase() === subdivision) : undefined

  return bySubdivision ?? inCountry.find((entry) => !entry.subdivisionCode)
}

const findLeaveLawSuggestion = (entry: ILeaveLawEntry | undefined, key: string): ILeaveLawSuggestion | undefined =>
  entry?.suggestedLeaveTypes?.find((suggestion) => suggestion.key === key)

const INTERVALS: Record<string, ShortestLeaveIntervalEnum> = {
  'FULL-DAY': ShortestLeaveIntervalEnum.fullDay,
  'HALF-DAY': ShortestLeaveIntervalEnum.halfDay,
  'ONE-HOUR': ShortestLeaveIntervalEnum.oneHour,
  'HALF-HOUR': ShortestLeaveIntervalEnum.halfHour,
  'FIFTEEN-MINUTES': ShortestLeaveIntervalEnum.fifteenMinutes,
}

const SUB_DAY_INTERVALS = [ShortestLeaveIntervalEnum.oneHour, ShortestLeaveIntervalEnum.halfHour, ShortestLeaveIntervalEnum.fifteenMinutes]

/**
 * Freemium cannot hold a sub-day interval at all: PlanLimitsValidator.validateLeavePolicy throws
 * for it, and it throws *before* the consumer's own clamp runs - so Illinois' hourly statutory PTO
 * would leave a Freemium company with a leave type and no policy. Every other plan is left alone;
 * leave-policy-enabled-v3 already downgrades where the plan or feature flag requires it.
 */
const clampShortestLeaveInterval = (interval: ShortestLeaveIntervalEnum, plan?: SubscriptionPlan | null): ShortestLeaveIntervalEnum => {
  if (plan !== 'Freemium') return interval
  return SUB_DAY_INTERVALS.includes(interval) ? ShortestLeaveIntervalEnum.halfDay : interval
}

/**
 * What we grant where the jurisdiction mandates nothing. Many US states - Montana among them -
 * publish a 0 for PTO or sick leave, which is a statement about the law, not a sensible company
 * policy: taken literally it hands a new company a leave type with no days in it.
 *
 * Only an exact 0 is lifted. A jurisdiction that mandates 3 days keeps its 3.
 */
const DAYS_WHERE_LAW_MANDATES_NONE: Record<string, number> = {
  [LeaveLawKeyEnum.paidTimeOff]: 12,
  [LeaveLawKeyEnum.sick]: 5,
}

/**
 * The quota a statutory entitlement is worth.
 *
 * Exported because the onboarding wizard seeds its leave types from raw feed suggestions rather
 * than from built policies - deriving the number in both places is how they would drift.
 */
// Unlimited wins over the number: Slovenia's sick leave is both 365 and unlimited.
export const statutoryDaysPerYear = (suggestion: Pick<ILeaveLawSuggestion, 'key' | 'daysPerYear' | 'hasUnlimitedDays'>): number =>
  suggestion.hasUnlimitedDays ? 0 : suggestion.daysPerYear || DAYS_WHERE_LAW_MANDATES_NONE[suggestion.key] || 0

/**
 * A statutory entitlement as leave-policy props, or undefined when the feed has no such
 * entitlement for the place.
 */
const leaveLawPolicy = (suggestion: ILeaveLawSuggestion | undefined, plan?: SubscriptionPlan | null): ILeaveLawPolicy | undefined => {
  if (!suggestion) return undefined

  const interval = INTERVALS[suggestion.shortestLeaveIntervalEnum] ?? ShortestLeaveIntervalEnum.fullDay

  return {
    daysPerYear: statutoryDaysPerYear(suggestion),
    hasUnlimitedDays: suggestion.hasUnlimitedDays,
    isApprovalRequired: suggestion.isApprovalRequired,
    shortestLeaveInterval: clampShortestLeaveInterval(interval, plan),
  }
}

/** Shared by both lookups: the same feed, place and plan, differing only in what is being looked up. */
export interface ILeaveLawLookup {
  laws: LeaveLawsResponse | undefined
  place: IPlace
  plan?: SubscriptionPlan | null
}

/** The statutory policy a company leave type should be seeded with, in one call. */
export const leaveLawPolicyForLeaveType = ({ laws, place, leaveTypeName, plan }: ILeaveLawLookup & { leaveTypeName?: string | null }): ILeaveLawPolicy | undefined =>
  laws ? leaveLawPolicy(findLeaveLawSuggestionForLeaveType(findLeaveLaw(laws, place), leaveTypeName), plan) : undefined

/** The law for one place and one entitlement, in one call. */
export const leaveLawPolicyFor = ({ laws, place, key, plan }: ILeaveLawLookup & { key: string }): ILeaveLawPolicy | undefined =>
  laws ? leaveLawPolicy(findLeaveLawSuggestion(findLeaveLaw(laws, place), key), plan) : undefined
